import {
    GroupCollaborationEntityOwner, GroupCollaborationEntityType, GroupCollaborationTemplateFilter, GroupCollaborationWorkshopDuration, PeriodType, RequestStatus,
    WorkshopBackgroundImage, WorkshopCanvasItemType, WorkshopExternalContentType, WorkshopPhaseContentCreationRule, WorkshopPhaseContentRowLayout,
    WorkshopPrioritizationVotingType, WorkshopQuestionnaireQuestionType, WorkshopRatingPhaseWeightedLevel, WorkshopResultPresentationType, WorkshopVersionHistoryChangeType,
    WorkshopWidgetStatus, WorkshopWidgetType, defaults, entities
} from 'typed/api';
import { Action } from 'typed/actions';
import {
    childrenReducer, combineReducers, entityAddAllReducer, entityAddReducer, entityDeleteAllReducer, entityDeleteReducer, entityEditAllReducer,
    entityEditReducer, entityLoadAllReducer, entityLoadManyReducer, entityLoadReducer, entityVoidAllReducer, networkActionReducer,
    sortableEntitiesReducer, sortedEntitiesReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { ActionType } from 'utils/constants';
import { Period, deepAssign } from 'typed/utils';

interface GroupCollaborationRestorePointOptions {
    entityType?: GroupCollaborationEntityType | null;
    owner?: GroupCollaborationEntityOwner;
    templateFilter?: GroupCollaborationTemplateFilter;
    workshopDuration?: GroupCollaborationWorkshopDuration;
}

const
    w = defaults.WorkshopReport,
    wn = defaults.NetworkArrayOf(w),
    wbia = defaults.NetworkArrayOf(defaults.WorkshopBackgroundImage),
    wpra: entities.WorkshopParticipantRole[] = [],
    wp = defaults.WorkshopParticipant,
    wpa: entities.WorkshopParticipant[] = [],
    wpcr = defaults.WorkshopPhaseContentRowReport,
    wpcrn = defaults.NetworkArrayOf(wpcr),
    ww = defaults.WorkshopWidgetReport,
    wwa: entities.WorkshopWidgetReport[] = [],
    wihn = w.ItemHistory,
    wirhn = w.ItemRatingHistory,
    wvc: entities.WorkshopVideoCall | null = null,
    wphr = defaults.WorkshopPhaseReport,
    wphra: entities.WorkshopPhaseReport[] = [],
    wppra: entities.WorkshopPhaseParticipantRole[] = [],
    wcia: entities.WorkshopCanvasItem[] = [],
    wic = defaults.WorkshopItemCategory,
    wica: entities.WorkshopItemCategory[] = [],
    wicc = defaults.WorkshopItemCategoryConnection,
    wicca: entities.WorkshopItemCategoryConnection[] = [],
    wpsa: entities.WorkshopWidgetParticipantStatus[] = [],
    wqfa: entities.WorkshopQuestionnaireFilter[] = [],
    wqq = defaults.WorkshopQuestionnaireQuestionReport,
    wqqa: entities.WorkshopQuestionnaireQuestionReport[] = [],
    wqoa: entities.WorkshopQuestionnaireOption[] = [],
    wqra: entities.WorkshopQuestionnaireResponse[] = [],
    wpect = defaults.NetworkObjectOf(''),
    wia: entities.WorkshopItem[] = [],
    wrps = defaults.WorkshopRatingPhaseSourceReport,
    wrpss: entities.WorkshopRatingPhaseSourceReport[] = [],
    wrpcws: entities.WorkshopRatingPhaseCategoryWeight[] = [],
    wira: entities.WorkshopItemRating[] = [],
    wpgba: entities.WorkshopPhaseGridBlock[] = [],
    wpcaa: entities.WorkshopPhaseComparisonAxis[] = [],
    wpcda: entities.WorkshopPhaseComparisonDimension[] = [],
    wrpsa: entities.WorkshopResultPhaseSource[] = [],
    wwigta: entities.WorkshopWidgetIdeaGenerationTheme[] = [],
    wcbi = defaults.NetworkObjectOf(''),

    wm = defaults.WorkspaceMindmapReport,
    wms = defaults.NetworkArrayOf(wm),
    wmnts: entities.WorkspaceMindmapNodeType[] = [],
    wmns = defaults.NetworkPaginationSliceOf(defaults.WorkspaceMindmapNode),

    gcrp: { [key: string]: GroupCollaborationRestorePointOptions | undefined; } = {},

    getWorkshopIndex = (state: entities.NetworkArray<entities.WorkshopReport> | undefined, action: Action): number => {
        if (!action.payload) { return -1; }
        const defaultState = state || wn;

        if (action.type.includes('WORKSHOP_QUESTIONNAIRE_RESPONSE')) {
            return defaultState.Data.findIndex(({ ContentRows }) => !!ContentRows.Data.getByID((action.payload as any).grandGrandParentID));
        }

        if (action.type === 'SEND_WORKSHOP_INVITATIONS' ||
            action.type === 'TOGGLE_WORKSHOP_MULTI_USER_STATUS' ||
            action.type === 'MARK_WORKSHOP_AS_VISITED' ||
            action.type === 'STORE_WORKSHOP_SCREENSHOT' ||
            action.type === 'STORE_CUSTOM_WORKSHOP_BACKGROUND_IMAGE' ||
            action.type === 'LOAD_WORKSHOP_ITEM_HISTORY' ||
            action.type === 'LOAD_WORKSHOP_ITEM_RATING_HISTORY') {
            return defaultState.Data.getIndexByID(action.payload.entityID);
        }

        if (action.type === 'CATEGORIZE_WORKSHOP_WIDGET_ITEMS' ||
            action.type === 'LOAD_MANY_WORKSHOP_WIDGET_PARTICIPANT_STATUSES' ||
            action.type === 'ADD_WORKSHOP_WIDGET_PARTICIPANT_STATUS' ||
            action.type === 'DELETE_WORKSHOP_WIDGET_PARTICIPANT_STATUS' ||
            action.type === 'CLEAR_WORKSHOP_WIDGET_PARTICIPANT_STATUSES' ||
            action.type === 'EXTRACT_WORKSHOP_ITEM_CATEGORY_ITEMS_FROM_SOURCES' ||
            action.type === 'TOUCH_WORKSHOP_ITEM_CATEGORY_CONNECTION' ||
            action.type === 'LOAD_WORKSHOP_ITEM' ||
            action.type === 'ADD_WORKSHOP_ITEM' ||
            action.type === 'ADD_ALL_WORKSHOP_ITEMS' ||
            action.type === 'EDIT_WORKSHOP_ITEM' ||
            action.type === 'EDIT_ALL_WORKSHOP_ITEMS' ||
            action.type === 'DELETE_WORKSHOP_ITEM' ||
            action.type === 'DELETE_ALL_WORKSHOP_ITEMS' ||
            action.type.includes('WORKSHOP_ITEM_RATING') ||
            action.type.includes('WORKSHOP_ITEM_CATEGORIES') ||
            action.type.includes('WORKSHOP_ITEM_CATEGORY_CONNECTIONS') ||
            action.type.includes('WORKSHOP_PHASE_GRID_BLOCK') ||
            action.type.includes('WORKSHOP_QUESTIONNAIRE_FILTER') ||
            action.type.includes('WORKSHOP_QUESTIONNAIRE_QUESTION') ||
            action.type.includes('WORKSHOP_QUESTIONNAIRE_OPTION') ||
            action.type.includes('WORKSHOP_RATING_PHASE_SOURCES') ||
            action.type.includes('WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS') ||
            action.type.includes('WORKSHOP_RESULT_PHASE_SOURCES') ||
            action.type.includes('WORKSHOP_WIDGET_IDEA_GENERATION_THEMES')) {
            return defaultState.Data.getIndexByID((action.payload as any).grandGrandParentID);
        }

        if (action.type === 'LOAD_MANY_WORKSHOP_PHASE_PARTICIPANT_ROLES' ||
            action.type === 'CALCULATE_WORKSHOP_WIDGET_RELATIVE_ITEM_RATINGS' ||
            action.type.includes('WORKSHOP_WIDGET') ||
            action.type.includes('WORKSHOP_CANVAS_ITEM') ||
            action.type.includes('WORKSHOP_PHASE_PARTICIPANT_ROLE') ||
            action.type.includes('WORKSHOP_PHASE_EXTERNAL_CONTENT') ||
            action.type.includes('WORKSHOP_PHASE_COMPARISON_AXES') ||
            action.type.includes('WORKSHOP_PHASE_COMPARISON_DIMENSIONS')) {
            return defaultState.Data.getIndexByID(action.payload?.grandParentID);
        }

        return defaultState.Data.getIndexByID(action.payload?.parentID);
    };

export default combineReducers({
    workshopSystemTemplates: entityLoadAllReducer(wn, 'LOAD_ALL_SYSTEM_WORKSHOP_TEMPLATES'),
    workshops: sortedEntitiesReducer(wn, 'Name', switchReducers(wn,
        entityLoadAllReducer(wn, 'LOAD_ALL_WORKSHOPS'),
        (state, action) => {
            let nextState: entities.NetworkArray<entities.WorkshopReport> = state || wn;

            if (action.type === 'LOAD_WORKSHOP' ||
                action.type === 'RECREATE_WORKSHOP' ||
                action.type === 'TOGGLE_WORKSHOP_MULTI_USER_STATUS' ||
                action.type === 'CLEAR_ALL_WORKSHOP_DATA') {
                return entityLoadReducer(nextState, action.type)(state, action);
            }

            if (action.type === 'LOAD_MANY_WORKSHOPS') {
                return entityLoadManyReducer(nextState, action.type)(state, action);
            }

            if (action.type === 'LOAD_ALL_WORKSHOP_PHASE_CONTENT_ROWS' || action.type === 'LOAD_WORKSHOP_PHASE_CONTENT_ROWS') {
                const workshopIDs = action.type === 'LOAD_ALL_WORKSHOP_PHASE_CONTENT_ROWS' ? nextState.Data.map(({ ID }) => ID) : action.payload.workshopIDs;

                for (let index = 0; index < workshopIDs.length; index++) {
                    const
                        workshopID = workshopIDs[index],
                        workshop = nextState.Data.getByID(workshopID);

                    if (workshop) {
                        const phaseIDs = workshop.Phases.map(({ ID }) => ID);

                        nextState = deepAssign(nextState, {
                            Data: {
                                [nextState.Data.getIndexByID(workshopID)]: {
                                    ContentRows: {
                                        Custom: {
                                            status: action.payload.status
                                        },
                                        ...action.payload.status === RequestStatus.SUCCEEDED ?
                                            { Data: (action.payload.response as entities.WorkshopPhaseContentRowReport[]).filter(({ PhaseID }) => phaseIDs.includes(PhaseID)) } :
                                            {}
                                    }
                                }
                            }
                        });
                    }
                }

                return nextState;
            }

            return nextState;
        },
        entityAddReducer(wn, 'ADD_WORKSHOP'),
        entityAddReducer(wn, 'ADD_WORKSHOP_TEMPLATE'),
        entityEditReducer(wn, 'EDIT_WORKSHOP'),
        entityAddAllReducer(wn, 'ADD_ALL_WORKSHOPS'),
        childrenReducer(wn,
            switchReducers(w,
                combineReducers(w, {
                    ParticipantRoles: switchReducers(wpra,
                        entityLoadManyReducer(wpra, 'LOAD_MANY_WORKSHOP_PARTICIPANT_ROLES'),
                        entityAddReducer(wpra, 'ADD_WORKSHOP_PARTICIPANT_ROLE'),
                        entityEditReducer(wpra, 'EDIT_WORKSHOP_PARTICIPANT_ROLE'),
                        entityDeleteReducer(wpra, 'DELETE_WORKSHOP_PARTICIPANT_ROLE')
                    ),
                    Participants: switchReducers(wpa,
                        entityLoadManyReducer(wpa, 'LOAD_MANY_WORKSHOP_PARTICIPANTS'),
                        entityAddReducer(wpa, 'ADD_WORKSHOP_PARTICIPANT'),
                        entityEditReducer(wpa, 'EDIT_WORKSHOP_PARTICIPANT'),
                        childrenReducer(wpa,
                            switchReducers(wp,
                                networkActionReducer(wp, 'TOUCH_WORKSHOP_PARTICIPANT'),
                                (state, action) => {
                                    const defaultState = state || wp;

                                    if (action.type === 'TOUCH_WORKSHOP_PARTICIPANT' && action.payload.status === RequestStatus.SUCCEEDED) {
                                        return deepAssign(defaultState, {
                                            FirstVisit: defaultState.FirstVisit || new Date(),
                                            LastVisit: new Date()
                                        });
                                    }

                                    return defaultState;
                                }
                            ),
                            (state, action) => action.payload ? state.getIndexByID(action.payload.entityID) : -1
                        ),
                        (state, action) => {
                            let nextState = state || wpa;

                            if (action.type === 'ADD_ALL_WORKSHOP_PARTICIPANTS') {
                                nextState = entityAddAllReducer(wpa, 'ADD_ALL_WORKSHOP_PARTICIPANTS')(state, action);

                                if (action.payload.status === RequestStatus.SUCCEEDED) {
                                    return nextState.groupBy(({ ID }) => ID).map(group => group.first()!);
                                }
                            }

                            if (action.type === 'SEND_WORKSHOP_INVITATIONS' && action.payload.status === RequestStatus.SUCCEEDED) {
                                return nextState.map(participant =>
                                    action.payload.invitedUsers.includes(participant.UserID) ? deepAssign(participant, { InviteSent: new Date() }) : participant
                                );
                            }

                            if (action.type === 'DELETE_WORKSHOP_PARTICIPANT_ROLE' && action.payload.status === RequestStatus.SUCCEEDED) {
                                return nextState.map(participant => deepAssign(participant, {
                                    RoleID: participant.RoleID !== action.payload.entityID ? participant.RoleID : null
                                }));
                            }

                            return nextState;
                        },
                        entityDeleteAllReducer(wpa, 'DELETE_ALL_WORKSHOP_PARTICIPANTS')
                    ),
                    Phases: sortableEntitiesReducer(wphra, null, switchReducers(wphra,
                        entityLoadAllReducer(wphra, 'LOAD_WORKSHOP_PHASES'),
                        (state, action) => {
                            const nextState = state || wphra;

                            if (action.type === 'LOAD_WORKSHOP_PHASE' ||
                                action.type === 'CONVERT_RATED_PHASE_ITEMS_TO_CATEGORIES') {
                                return entityLoadReducer(wphra, action.type)(state, action);
                            }

                            if (action.type === 'CHANGE_WORKSHOP_PHASE_PUBLICATION_STATUS') {
                                if (action.payload.status !== RequestStatus.SUCCEEDED) {
                                    return deepAssign(nextState, {
                                        [nextState.getIndexByID(action.payload.entityID)]: {
                                            Custom: {
                                                status: action.payload.status
                                            },
                                            IsPublished: action.payload.published
                                        }
                                    });
                                }
                                else {
                                    return entityLoadReducer(wphra, action.type)(state, action);
                                }
                            }

                            return nextState;
                        },
                        entityAddReducer(wphra, 'ADD_WORKSHOP_PHASE'),
                        entityEditReducer(wphra, 'EDIT_WORKSHOP_PHASE'),
                        entityEditAllReducer(wphra, 'EDIT_ALL_WORKSHOP_PHASES'),
                        childrenReducer(wphra,
                            combineReducers(wphr, {
                                ParticipantRoles: switchReducers(wppra,
                                    entityLoadManyReducer(wppra, 'LOAD_MANY_WORKSHOP_PHASE_PARTICIPANT_ROLES'),
                                    entityAddReducer(wppra, 'ADD_WORKSHOP_PHASE_PARTICIPANT_ROLE'),
                                    entityEditReducer(wppra, 'EDIT_WORKSHOP_PHASE_PARTICIPANT_ROLE'),
                                    entityDeleteAllReducer(wppra, 'DELETE_ALL_WORKSHOP_PHASE_PARTICIPANT_ROLES'),
                                    entityDeleteReducer(wppra, 'DELETE_WORKSHOP_PHASE_PARTICIPANT_ROLE')
                                ),
                                CanvasItems: switchReducers(wcia,
                                    entityLoadReducer(wcia, 'LOAD_WORKSHOP_CANVAS_ITEM'),
                                    entityAddReducer(wcia, 'ADD_WORKSHOP_CANVAS_ITEM'),
                                    entityEditReducer(wcia, 'EDIT_WORKSHOP_CANVAS_ITEM'),
                                    entityDeleteReducer(wcia, 'DELETE_WORKSHOP_CANVAS_ITEM'),
                                    (state, action) => {
                                        const nextState = state || wcia;

                                        if (action.type === 'DELETE_WORKSHOP_CANVAS_ITEM' && action.payload.status === RequestStatus.SUCCEEDED) {
                                            return nextState.map(item => deepAssign(item, { GroupID: item.GroupID !== action.payload.entityID ? item.GroupID : null }));
                                        }

                                        return nextState;
                                    }
                                )
                            }),
                            (state, action) => state.getIndexByID(action.payload?.parentID)
                        ),
                        (state, action) => {
                            let nextState: entities.WorkshopPhaseReport[] = state || wphra;

                            if (action.type === 'CLONE_WORKSHOP_PHASE') {
                                if (action.payload.status !== RequestStatus.SUCCEEDED) {
                                    return deepAssign(nextState, {
                                        [nextState.getIndexByID(action.payload.entityID)]: {
                                            Custom: {
                                                status: action.payload.status
                                            }
                                        }
                                    });
                                }
                                else {
                                    return action.payload.response;
                                }
                            }

                            if (action.type === 'STORE_CUSTOM_WORKSHOP_PHASE_BACKGROUND_IMAGE') {
                                nextState = deepAssign(nextState, {
                                    [nextState.getIndexByID(action.payload.entityID)]: {
                                        Custom: {
                                            status: action.payload.status
                                        },
                                        ...action.payload.status === RequestStatus.SUCCEEDED ?
                                            { CustomBackgroundImageBlobStorageUrl: action.payload.response } :
                                            {}
                                    }
                                });
                            }

                            return nextState;
                        },
                        entityDeleteReducer(wphra, 'DELETE_WORKSHOP_PHASE')
                    )),
                    ContentRows: sortableEntitiesReducer(wpcrn, 'PhaseID', switchReducers(wpcrn,
                        entityLoadReducer(wpcrn, 'LOAD_WORKSHOP_PHASE_CONTENT_ROW'),
                        entityAddReducer(wpcrn, 'ADD_WORKSHOP_PHASE_CONTENT_ROW'),
                        entityEditReducer(wpcrn, 'EDIT_WORKSHOP_PHASE_CONTENT_ROW'),
                        childrenReducer(wpcrn,
                            combineReducers(wpcr, {
                                Widgets: switchReducers(wwa,
                                    entityLoadReducer(wwa, 'LOAD_WORKSHOP_WIDGET'),
                                    entityAddReducer(wwa, 'ADD_WORKSHOP_WIDGET'),
                                    entityEditReducer(wwa, 'EDIT_WORKSHOP_WIDGET'),
                                    entityLoadReducer(wwa, 'GENERATE_WORKSHOP_WIDGET_CONTENTS'),
                                    entityLoadReducer(wwa, 'SET_WORKSHOP_WIDGET_STATUS'),
                                    entityLoadReducer(wwa, 'CALCULATE_WORKSHOP_WIDGET_RELATIVE_ITEM_RATINGS'),
                                    childrenReducer(wwa,
                                        combineReducers(ww, {
                                            ParticipantStatuses: switchReducers(wpsa,
                                                entityLoadManyReducer(wpsa, 'LOAD_MANY_WORKSHOP_WIDGET_PARTICIPANT_STATUSES'),
                                                entityAddReducer(wpsa, 'ADD_WORKSHOP_WIDGET_PARTICIPANT_STATUS'),
                                                entityDeleteReducer(wpsa, 'DELETE_WORKSHOP_WIDGET_PARTICIPANT_STATUS')
                                            ),
                                            ItemCategories: sortableEntitiesReducer(wica, 'IsSubcategory', switchReducers(wica,
                                                entityAddAllReducer(wica, 'ADD_ALL_WORKSHOP_ITEM_CATEGORIES'),
                                                entityEditAllReducer(wica, 'EDIT_ALL_WORKSHOP_ITEM_CATEGORIES'),
                                                entityDeleteAllReducer(wica, 'DELETE_ALL_WORKSHOP_ITEM_CATEGORIES'),
                                                childrenReducer(wica,
                                                    networkActionReducer(wic, 'EXTRACT_WORKSHOP_ITEM_CATEGORY_ITEMS_FROM_SOURCES'),
                                                    (state, action) => (state || wica).getIndexByID(action.payload.entityID)
                                                )
                                            )),
                                            ItemCategoryConnections: switchReducers(wicca,
                                                entityAddAllReducer(wicca, 'ADD_ALL_WORKSHOP_ITEM_CATEGORY_CONNECTIONS'),
                                                entityEditAllReducer(wicca, 'EDIT_ALL_WORKSHOP_ITEM_CATEGORY_CONNECTIONS'),
                                                entityDeleteAllReducer(wicca, 'DELETE_ALL_WORKSHOP_ITEM_CATEGORY_CONNECTIONS'),
                                                childrenReducer(wicca,
                                                    switchReducers(wicc,
                                                        networkActionReducer(wicc, 'TOUCH_WORKSHOP_ITEM_CATEGORY_CONNECTION'),
                                                        (state, action) => {
                                                            const nextState = state || wicc;

                                                            if (action.type === 'TOUCH_WORKSHOP_ITEM_CATEGORY_CONNECTION' && action.payload.status === RequestStatus.SUCCEEDED) {
                                                                return deepAssign(nextState, { LastCheckDate: new Date() });
                                                            }

                                                            return nextState;
                                                        }
                                                    ),
                                                    (state, action) => (state || wicca).getIndexByID(action.payload?.entityID)
                                                ),
                                                (state, action) => {
                                                    const nextState = state || wicca;

                                                    if (action.type === 'EXTRACT_WORKSHOP_ITEM_CATEGORY_ITEMS_FROM_SOURCES' && action.payload.status === RequestStatus.SUCCEEDED) {
                                                        return nextState.map(connection =>
                                                            connection.ParentCategoryID === action.payload.entityID ?
                                                                deepAssign(connection, { LastCheckDate: new Date() }) :
                                                                connection
                                                        );
                                                    }

                                                    return nextState;
                                                }
                                            ),
                                            Items: sortedEntitiesReducer(wia, 'Name', switchReducers(wia,
                                                entityLoadReducer(wia, 'LOAD_WORKSHOP_ITEM'),
                                                entityAddReducer(wia, 'ADD_WORKSHOP_ITEM'),
                                                entityAddAllReducer(wia, 'ADD_ALL_WORKSHOP_ITEMS'),
                                                entityEditReducer(wia, 'EDIT_WORKSHOP_ITEM'),
                                                entityEditAllReducer(wia, 'EDIT_ALL_WORKSHOP_ITEMS'),
                                                (state, action) => {
                                                    const nextState: entities.WorkshopItem[] = (state || wia).copy();

                                                    if (action.type === 'DELETE_ALL_WORKSHOP_ITEM_CATEGORIES' && action.payload.status === RequestStatus.SUCCEEDED) {
                                                        return nextState.map(item => deepAssign(item, {
                                                            CategoryID: !action.payload.entityIDs.includes(item.CategoryID) ? item.CategoryID : null
                                                        }));
                                                    }

                                                    if (action.type === 'DELETE_WORKSHOP_ITEM' && action.payload.status === RequestStatus.SUCCEEDED) {
                                                        return nextState.filter(({ MergedToItemID, FromCrossPollinationItemID, ToCrossPollinationItemID }) =>
                                                            MergedToItemID !== action.payload.entityID &&
                                                            FromCrossPollinationItemID !== action.payload.entityID &&
                                                            ToCrossPollinationItemID !== action.payload.entityID
                                                        );
                                                    }

                                                    if (action.type === 'DELETE_ALL_WORKSHOP_ITEMS' && action.payload.status === RequestStatus.SUCCEEDED) {
                                                        return nextState.filter(({ MergedToItemID, FromCrossPollinationItemID, ToCrossPollinationItemID }) =>
                                                            !action.payload.entityIDs.includes(MergedToItemID) &&
                                                            !action.payload.entityIDs.includes(FromCrossPollinationItemID) &&
                                                            !action.payload.entityIDs.includes(ToCrossPollinationItemID)
                                                        );
                                                    }

                                                    return nextState;
                                                },
                                                entityDeleteReducer(wia, 'DELETE_WORKSHOP_ITEM'),
                                                entityDeleteAllReducer(wia, 'DELETE_ALL_WORKSHOP_ITEMS')
                                            )),
                                            RatingPhaseSources: switchReducers(wrpss,
                                                entityLoadManyReducer(wrpss, 'LOAD_MANY_WORKSHOP_RATING_PHASE_SOURCES'),
                                                entityAddAllReducer(wrpss, 'ADD_ALL_WORKSHOP_RATING_PHASE_SOURCES'),
                                                entityEditAllReducer(wrpss, 'EDIT_ALL_WORKSHOP_RATING_PHASE_SOURCES'),
                                                entityDeleteAllReducer(wrpss, 'DELETE_ALL_WORKSHOP_RATING_PHASE_SOURCES'),
                                                (state, action) => {
                                                    let nextState = state || wrpss;

                                                    if (action.type === 'ADD_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS' ||
                                                        action.type === 'EDIT_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS') {
                                                        action.payload.entities
                                                            .groupBy((entity: entities.WorkshopRatingPhaseCategoryWeight) => {
                                                                if (entity.RatingPhaseSourceID) { return entity.RatingPhaseSourceID; }
                                                                return nextState.find(({ CategoryWeights }) => CategoryWeights.getByID(entity.ID))?.ID || 0;
                                                            })
                                                            .forEach((group: any) => {
                                                                if (!group.key) { return; }

                                                                const fakedAction = deepAssign(action, { payload: { parentID: group.key } });
                                                                nextState = childrenReducer(wrpss,
                                                                    combineReducers(wrps, {
                                                                        CategoryWeights: switchReducers(wrpcws,
                                                                            entityAddAllReducer(wrpcws, 'ADD_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS'),
                                                                            entityEditAllReducer(wrpcws, 'EDIT_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS'),
                                                                            entityDeleteAllReducer(wrpcws, 'DELETE_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS')
                                                                        )
                                                                    }),
                                                                    (state, action) => state.getIndexByID(action.payload?.parentID)
                                                                )(nextState, fakedAction);
                                                            });
                                                    }

                                                    if (action.type === 'DELETE_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS') {
                                                        action.payload.entityIDs
                                                            .groupBy((entityID: number) => nextState.find(({ CategoryWeights }) => CategoryWeights.getByID(entityID))?.ID || 0)
                                                            .forEach((group: any) => {
                                                                if (!group.key) { return; }

                                                                const fakedAction = deepAssign(action, { payload: { parentID: group.key } });
                                                                nextState = childrenReducer(wrpss,
                                                                    combineReducers(wrps, {
                                                                        CategoryWeights: switchReducers(wrpcws,
                                                                            entityAddAllReducer(wrpcws, 'ADD_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS'),
                                                                            entityEditAllReducer(wrpcws, 'EDIT_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS'),
                                                                            entityDeleteAllReducer(wrpcws, 'DELETE_ALL_WORKSHOP_RATING_PHASE_CATEGORY_WEIGHTS')
                                                                        )
                                                                    }),
                                                                    (state, action) => state.getIndexByID(action.payload?.parentID)
                                                                )(nextState, fakedAction);
                                                            });
                                                    }

                                                    return nextState;
                                                }
                                            ),
                                            ItemRatings: switchReducers(wira,
                                                entityAddReducer(wira, 'ADD_WORKSHOP_ITEM_RATING'),
                                                entityEditReducer(wira, 'EDIT_WORKSHOP_ITEM_RATING'),
                                                (state, action) => {
                                                    const defaultState = state || wira;

                                                    if (action.type === 'DELETE_WORKSHOP_ITEM_RATING') {
                                                        return entityDeleteReducer(wira, action.type)(state, deepAssign(action, { payload: { status: RequestStatus.SUCCEEDED } }));
                                                    }

                                                    if (action.type === 'CLEAR_WORKSHOP_ITEM_RATINGS') {
                                                        return wira;
                                                    }

                                                    return defaultState;
                                                }
                                            ),
                                            GridBlocks: switchReducers(wpgba,
                                                entityAddAllReducer(wpgba, 'ADD_ALL_WORKSHOP_PHASE_GRID_BLOCKS'),
                                                entityEditReducer(wpgba, 'EDIT_WORKSHOP_PHASE_GRID_BLOCK'),
                                                entityEditAllReducer(wpgba, 'EDIT_ALL_WORKSHOP_PHASE_GRID_BLOCKS')
                                            ),
                                            ComparisonAxes: sortableEntitiesReducer(wpcaa, null, switchReducers(wpcaa,
                                                entityAddAllReducer(wpcaa, 'ADD_ALL_WORKSHOP_PHASE_COMPARISON_AXES'),
                                                entityEditAllReducer(wpcaa, 'EDIT_ALL_WORKSHOP_PHASE_COMPARISON_AXES'),
                                                entityDeleteAllReducer(wpcaa, 'DELETE_ALL_WORKSHOP_PHASE_COMPARISON_AXES')
                                            )),
                                            ComparisonDimensions: sortableEntitiesReducer(wpcda, null, switchReducers(wpcda,
                                                entityAddAllReducer(wpcda, 'ADD_ALL_WORKSHOP_PHASE_COMPARISON_DIMENSIONS'),
                                                entityEditAllReducer(wpcda, 'EDIT_ALL_WORKSHOP_PHASE_COMPARISON_DIMENSIONS'),
                                                entityDeleteAllReducer(wpcda, 'DELETE_ALL_WORKSHOP_PHASE_COMPARISON_DIMENSIONS')
                                            )),
                                            QuestionnaireFilters: switchReducers(wqfa,
                                                entityAddReducer(wqfa, 'ADD_WORKSHOP_QUESTIONNAIRE_FILTER'),
                                                entityEditReducer(wqfa, 'EDIT_WORKSHOP_QUESTIONNAIRE_FILTER')
                                            ),
                                            QuestionnaireQuestions: sortableEntitiesReducer(wqqa, null, switchReducers(wqqa,
                                                entityLoadReducer(wqqa, 'LOAD_WORKSHOP_QUESTIONNAIRE_QUESTION'),
                                                (state, action) => {
                                                    let nextState = state || wqqa;

                                                    if (action.type === 'ADD_ALL_WORKSHOP_QUESTIONNAIRE_QUESTIONS') {
                                                        if (action.payload.status !== RequestStatus.SUCCEEDED) {
                                                            return entityAddAllReducer(wqqa, action.type)(nextState, action);
                                                        }
                                                        else {
                                                            for (let index = 0; index < action.payload.entities.length; index++) {
                                                                const entity = action.payload.entities[index];
                                                                nextState = deepAssign(nextState, {
                                                                    [nextState.getIndexByID(entity.ID)]: {
                                                                        ...action.payload.response[index],
                                                                        Custom: {
                                                                            internalID: entity.ID
                                                                        }
                                                                    }
                                                                });
                                                            }


                                                        }
                                                    }

                                                    return nextState;
                                                },
                                                entityEditAllReducer(wqqa, 'EDIT_ALL_WORKSHOP_QUESTIONNAIRE_QUESTIONS'),
                                                childrenReducer(wqqa,
                                                    combineReducers(wqq, {
                                                        Responses: switchReducers(wqra,
                                                            entityAddReducer(wqra, 'ADD_WORKSHOP_QUESTIONNAIRE_RESPONSE'),
                                                            entityEditReducer(wqra, 'EDIT_WORKSHOP_QUESTIONNAIRE_RESPONSE'),
                                                            (state, action) => {
                                                                const defaultState = state || wqra;

                                                                if (action.type === 'DELETE_ALL_WORKSHOP_QUESTIONNAIRE_OPTIONS') {
                                                                    return defaultState.filter(({ OptionID }) => !action.payload.entityIDs.includes(OptionID));
                                                                }

                                                                return defaultState;
                                                            }
                                                        )
                                                    }),
                                                    (state, action) => (state || wqqa).getIndexByID(action.payload?.parentID)
                                                ),
                                                entityDeleteAllReducer(wqqa, 'DELETE_ALL_WORKSHOP_QUESTIONNAIRE_QUESTIONS')
                                            )),
                                            ResultPhaseSources: switchReducers(wrpsa,
                                                entityAddAllReducer(wrpsa, 'ADD_ALL_WORKSHOP_RESULT_PHASE_SOURCES'),
                                                entityEditAllReducer(wrpsa, 'EDIT_ALL_WORKSHOP_RESULT_PHASE_SOURCES'),
                                                entityDeleteAllReducer(wrpsa, 'DELETE_ALL_WORKSHOP_RESULT_PHASE_SOURCES')
                                            ),
                                            IdeaGenerationThemes: sortableEntitiesReducer(wwigta, 'WidgetID', switchReducers(wwigta,
                                                entityAddAllReducer(wwigta, 'ADD_ALL_WORKSHOP_WIDGET_IDEA_GENERATION_THEMES'),
                                                entityEditAllReducer(wwigta, 'EDIT_ALL_WORKSHOP_WIDGET_IDEA_GENERATION_THEMES'),
                                                entityDeleteAllReducer(wwigta, 'DELETE_ALL_WORKSHOP_WIDGET_IDEA_GENERATION_THEMES')
                                            ))
                                        }),
                                        (state, action) => {
                                            if (action.type.includes('WORKSHOP_QUESTIONNAIRE_RESPONSE')) {
                                                return state.getIndexByID(action.payload.grandParentID);
                                            }
                                            else if (action.payload) {
                                                return state.getIndexByID(action.payload.parentID);
                                            }

                                            return -1;
                                        }
                                    ),
                                    (state, action) => {
                                        let nextState = state || wwa;

                                        if (action.type === 'CATEGORIZE_WORKSHOP_WIDGET_ITEMS') {
                                            return deepAssign(nextState, {
                                                [nextState.getIndexByID(action.payload.parentID)]: {
                                                    Custom: {
                                                        status: action.payload.status
                                                    }
                                                }
                                            });
                                        }

                                        if (action.type === 'EXTRACT_WORKSHOP_ITEM_CATEGORY_ITEMS_FROM_SOURCES' && action.payload.status === RequestStatus.SUCCEEDED) {
                                            return deepAssign(nextState, {
                                                [nextState.getIndexByID(action.payload.parentID)]: action.payload.response
                                            });
                                        }

                                        if (action.type === 'ADD_ALL_WORKSHOP_QUESTIONNAIRE_OPTIONS' ||
                                            action.type === 'EDIT_ALL_WORKSHOP_QUESTIONNAIRE_OPTIONS') {
                                            const widget = nextState.getByID(action.payload.parentID);

                                            if (widget) {
                                                const reducer = action.type === 'ADD_ALL_WORKSHOP_QUESTIONNAIRE_OPTIONS' ? entityAddAllReducer : entityEditAllReducer;

                                                (action.payload.entities as entities.WorkshopQuestionnaireOption[])
                                                    .groupBy(({ QuestionID }) => widget.QuestionnaireQuestions.getIndexByID(QuestionID))
                                                    .forEach((options) => {
                                                        const
                                                            optionState = widget.QuestionnaireQuestions[options.key]?.Options || wqoa,
                                                            filteredAction = deepAssign(action, {
                                                                payload: {
                                                                    entities: options,
                                                                    entityIDs: options.map(({ ID }) => ID)
                                                                }
                                                            });

                                                        nextState = deepAssign(nextState, {
                                                            [nextState.getIndexByID(action.payload.parentID)]: {
                                                                QuestionnaireQuestions: {
                                                                    [options.key]: {
                                                                        Options: sortableEntitiesReducer(wqoa, null, reducer(wqoa, action.type))(optionState, filteredAction)
                                                                    }
                                                                }
                                                            }
                                                        });
                                                    });
                                            }
                                        }

                                        if (action.type === 'DELETE_ALL_WORKSHOP_QUESTIONNAIRE_OPTIONS') {
                                            const widget = nextState.getByID(action.payload.parentID);

                                            if (widget) {
                                                return deepAssign(nextState, {
                                                    [nextState.getIndexByID(action.payload.parentID)]: {
                                                        QuestionnaireQuestions: widget.QuestionnaireQuestions.map((question) => {
                                                            if (action.payload.status !== RequestStatus.SUCCEEDED) {
                                                                return {
                                                                    Options: question.Options.map(option =>
                                                                        action.payload.entityIDs.includes(option.ID) ?
                                                                            deepAssign(option, { Custom: { status: action.payload.status } }) :
                                                                            option
                                                                    )
                                                                };
                                                            }
                                                            else {
                                                                return {
                                                                    Options: question.Options
                                                                        .filter(({ ID }) => !action.payload.entityIDs.includes(ID))
                                                                        .map((option, optionIndex) => deepAssign(option, { Sort: optionIndex + 1 })),
                                                                    Responses: question.Responses.filter(({ OptionID }) => !action.payload.entityIDs.includes(OptionID))
                                                                };
                                                            }
                                                        })
                                                    }
                                                });
                                            }
                                        }

                                        return nextState;
                                    },
                                    entityDeleteReducer(wwa, 'DELETE_WORKSHOP_WIDGET')
                                )
                            }),
                            (state, action) => {
                                if (action.type === 'LOAD_MANY_WORKSHOP_WIDGET_PARTICIPANT_STATUSES' ||
                                    action.type === 'ADD_WORKSHOP_WIDGET_PARTICIPANT_STATUS' ||
                                    action.type === 'DELETE_WORKSHOP_WIDGET_PARTICIPANT_STATUS' ||
                                    action.type === 'CATEGORIZE_WORKSHOP_WIDGET_ITEMS' ||
                                    action.type === 'EXTRACT_WORKSHOP_ITEM_CATEGORY_ITEMS_FROM_SOURCES' ||
                                    action.type.includes('WORKSHOP_WIDGET_IDEA_GENERATION_THEMES')) {
                                    return state.Data.getIndexByID((action.payload as any).grandParentID);
                                }
                                else if (action.type.includes('WORKSHOP_WIDGET')) {
                                    return state.Data.getIndexByID(action.payload.parentID);
                                }
                                else if (action.type.includes('WORKSHOP_QUESTIONNAIRE_RESPONSE')) {
                                    return state.Data.getIndexByID(action.payload.grandGrandParentID);
                                }
                                else if (action.payload) {
                                    return state.Data.getIndexByID(action.payload.grandParentID);
                                }

                                return -1;
                            }
                        ),
                        entityDeleteReducer(wpcrn, 'DELETE_WORKSHOP_PHASE_CONTENT_ROW'),
                        (state, action) => {
                            let nextState = state || wpcrn;

                            if (action.type === 'CLONE_WORKSHOP_PHASE' && action.payload.status === RequestStatus.SUCCEEDED ||
                                action.type === 'CHANGE_WORKSHOP_PHASE_PUBLICATION_STATUS' && action.payload.status === RequestStatus.SUCCEEDED && action.payload.published) {
                                return deepAssign(nextState, {
                                    Custom: {
                                        status: RequestStatus.UNSYNCED
                                    }
                                });
                            }

                            if (action.type === 'EDIT_WORKSHOP_WIDGET' && action.payload.status === RequestStatus.SUCCEEDED) {
                                const repositionedWidgets = nextState.Data.map(({ ID, Widgets }) => Widgets.filter(({ ContentRowID }) => ContentRowID !== ID)).flatten();

                                for (let widgetIndex = 0; widgetIndex < repositionedWidgets.length; widgetIndex++) {
                                    const
                                        widget = repositionedWidgets[widgetIndex],
                                        contentRow = nextState.Data.getByID(widget.ContentRowID);

                                    if (contentRow) {
                                        nextState = deepAssign(nextState, {
                                            Data: {
                                                [nextState.Data.getIndexByID(contentRow.ID)]: {
                                                    Widgets: {
                                                        [contentRow.Widgets.length]: widget
                                                    }
                                                }
                                            }
                                        });
                                    }
                                }

                                if (repositionedWidgets.length) {
                                    return deepAssign(nextState, {
                                        Data: nextState.Data.map(({ ID, Widgets }) => ({
                                            Widgets: Widgets.filter(({ ContentRowID }) => ContentRowID === ID).sortBy('RowPosition')
                                        }))
                                    });
                                }
                            }

                            return nextState;
                        }
                    )),
                    ItemHistory: (state, action) => {
                        let nextState: entities.NetworkArray<entities.WorkshopItemHistory> = state || wihn;

                        if (action.type === 'LOAD_WORKSHOP_ITEM_HISTORY') {
                            nextState = deepAssign(nextState, { Custom: { status: action.payload.status } });

                            if (action.payload.status === RequestStatus.SUCCEEDED) {
                                nextState = deepAssign(nextState, { Data: action.payload.response });
                            }
                        }

                        if (action.type === 'EXTRACT_WORKSHOP_ITEM_CATEGORY_ITEMS_FROM_SOURCES' ||
                            action.type === 'ADD_ALL_WORKSHOP_ITEMS' ||
                            action.type === 'EDIT_ALL_WORKSHOP_ITEMS' ||
                            action.type === 'ADD_WORKSHOP_ITEM' ||
                            action.type === 'EDIT_WORKSHOP_ITEM' ||
                            action.type === 'DELETE_WORKSHOP_ITEM') {
                            const today = +new Period(PeriodType.DAY).start;

                            if (action.payload.status === RequestStatus.SUCCEEDED && !nextState.Data.some(({ ChangeDate }) => +ChangeDate >= today)) {
                                return deepAssign(nextState, { Custom: { status: RequestStatus.UNSYNCED } });
                            }
                        }

                        return nextState;
                    },
                    ItemRatingHistory: (state, action) => {
                        let nextState: entities.NetworkArray<entities.WorkshopItemRatingHistory> = state || wirhn;

                        if (action.type === 'LOAD_WORKSHOP_ITEM_RATING_HISTORY') {
                            nextState = deepAssign(nextState, { Custom: { status: action.payload.status } });

                            if (action.payload.status === RequestStatus.SUCCEEDED) {
                                nextState = deepAssign(nextState, { Data: action.payload.response });
                            }
                        }

                        if (action.type === 'ADD_WORKSHOP_ITEM_RATING' ||
                            action.type === 'EDIT_WORKSHOP_ITEM_RATING' ||
                            action.type === 'DELETE_WORKSHOP_ITEM_RATING' ||
                            action.type === 'CLEAR_WORKSHOP_ITEM_RATINGS') {
                            const today = +new Period(PeriodType.DAY).start;

                            if (action.payload.status === RequestStatus.SUCCEEDED && !nextState.Data.some(({ ChangeDate }) => +ChangeDate >= today)) {
                                return deepAssign(nextState, { Custom: { status: RequestStatus.UNSYNCED } });
                            }
                        }

                        return nextState;
                    },
                    VideoCall: (state, action) => {
                        let nextState = state || wvc;

                        if (action.type === 'ADD_WORKSHOP_VIDEO_CALL' || action.type === 'EDIT_WORKSHOP_VIDEO_CALL') {
                            nextState = action.payload.entity || nextState;
                        }

                        if (action.type === 'LOAD_WORKSHOP_VIDEO_CALL' ||
                            action.type === 'ADD_WORKSHOP_VIDEO_CALL' ||
                            action.type === 'EDIT_WORKSHOP_VIDEO_CALL' ||
                            action.type === 'DELETE_WORKSHOP_VIDEO_CALL') {
                            if (nextState) {
                                nextState = deepAssign(nextState, { Custom: { status: action.payload.status } });
                            }

                            if (action.payload.status === RequestStatus.SUCCEEDED) {
                                switch (action.type) {
                                    case 'LOAD_WORKSHOP_VIDEO_CALL':
                                        return action.payload.response;

                                    case 'ADD_WORKSHOP_VIDEO_CALL':
                                        return deepAssign(nextState, { ID: action.payload.response });

                                    case 'DELETE_WORKSHOP_VIDEO_CALL':
                                        return null;

                                    default:
                                        return nextState;
                                }
                            }
                        }

                        return nextState;
                    }
                }),
                (state, action) => {
                    let nextState = state || w;

                    if (action.type.includes('WORKSHOP') && action.payload.status === RequestStatus.FAILED) {
                        return deepAssign(nextState, { Custom: { status: RequestStatus.FAILED } });
                    }

                    if (action.type === 'MARK_WORKSHOP_AS_VISITED') {
                        return deepAssign(nextState, { Custom: { visited: true } });
                    }

                    if (action.type === 'LOAD_WORKSHOP_PHASES') {
                        return deepAssign(nextState, { Custom: { phaseStatus: action.payload.status } });
                    }

                    if (action.type === 'DELETE_ALL_WORKSHOP_PARTICIPANTS' && action.payload.status === RequestStatus.SUCCEEDED) {
                        return deepAssign(nextState, {
                            ContentRows: {
                                Data: nextState.ContentRows.Data.map(({ Widgets }) => ({
                                    Widgets: Widgets.map(({ ParticipantStatuses }) => ({
                                        ParticipantStatuses: ParticipantStatuses.filter(({ ParticipantID }) => !action.payload.entityIDs.includes(ParticipantID))
                                    }))
                                }))
                            }
                        });
                    }

                    if ((action.type === 'LOAD_WORKSHOP_ITEM_HISTORY' || action.type === 'LOAD_WORKSHOP_ITEM_RATING_HISTORY') &&
                        action.payload.status === RequestStatus.SUCCEEDED) {
                        const versions = nextState.ItemHistory.Data
                            .map(({ ChangeDate }) => +new Period(PeriodType.DAY, ChangeDate).start)
                            .concat(...nextState.ItemRatingHistory.Data.map(({ ChangeDate }) => +new Period(PeriodType.DAY, ChangeDate).start))
                            .unique()
                            .sortBy(value => -value)
                            .or([+new Period(PeriodType.DAY, nextState.Created).start]);

                        nextState = deepAssign(nextState, {
                            Custom: {
                                versions
                            }
                        });

                        if (nextState.ItemHistory.Custom.status === RequestStatus.SUCCEEDED &&
                            nextState.ItemRatingHistory.Custom.status === RequestStatus.SUCCEEDED &&
                            (!nextState.Custom.version || !versions.includes(nextState.Custom.version))) {
                            nextState = deepAssign(nextState, {
                                Custom: {
                                    version: versions.first() || null
                                }
                            });
                        }
                    }

                    if (action.type === 'CLEAR_WORKSHOP_WIDGET_PARTICIPANT_STATUSES' && action.payload.status === RequestStatus.SUCCEEDED) {
                        return deepAssign(nextState, {
                            ContentRows: {
                                Data: nextState.ContentRows.Data.map(({ Widgets }) => ({
                                    Widgets: Widgets
                                        .filter(({ ID }) => action.payload.widgetIDs.includes(ID))
                                        .map(({ ID }) => ({
                                            [Widgets.getIndexByID(ID)]: {
                                                ParticipantStatuses: []
                                            }
                                        }))
                                        .reduce((widgetMap, change) => ({ ...widgetMap, ...change }), {})
                                }))
                            }
                        });
                    }

                    const versionChangeRegex = /^(ADD|EDIT|DELETE)(_ALL)?_WORKSHOP_ITEM(_RATING)?(S)?$/;

                    if ((versionChangeRegex.test(action.type) || action.type === 'CLEAR_WORKSHOP_ITEM_RATINGS') && action.payload.status === RequestStatus.SUCCEEDED) {
                        return deepAssign(nextState, {
                            Custom: {
                                version: null
                            }
                        });
                    }

                    return nextState;
                }
            ),
            getWorkshopIndex
        ),
        (state, action) => {
            let nextState = state || wn;

            if (action.type === ActionType.SET_INITIAL_STARTING_POINT_SUCCESS) {
                return deepAssign(nextState, { Custom: { status: RequestStatus.UNSYNCED } });
            }

            if (action.type === 'STORE_WORKSHOP_SCREENSHOT') {
                return deepAssign(nextState, {
                    Data: {
                        [nextState.Data.getIndexByID(action.payload.entityID)]: {
                            Custom: {
                                status: action.payload.status
                            },
                            ...action.payload.statusID === RequestStatus.SUCCEEDED ? { PreviewBlobStorageUrl: action.payload.response } : {}
                        }
                    }
                });
            }

            if (action.type === 'STORE_CUSTOM_WORKSHOP_BACKGROUND_IMAGE') {
                return deepAssign(nextState, {
                    Data: {
                        [nextState.Data.getIndexByID(action.payload.entityID)]: {
                            Custom: {
                                status: action.payload.status
                            },
                            ...action.payload.status === RequestStatus.SUCCEEDED ? { CustomBackgroundImageBlobStorageUrl: action.payload.response } : {}
                        }
                    }
                });
            }

            if (action.type === 'CLONE_WORKSHOP') {
                nextState = deepAssign(nextState, {
                    Data: {
                        [nextState.Data.getIndexByID(action.payload.entityID)]: {
                            Custom: {
                                cloningStatus: action.payload.status
                            }
                        }
                    }
                });
            }

            if (action.type === 'CONVERT_WORKSHOP_TO_TEMPLATE') {
                nextState = deepAssign(nextState, {
                    Data: {
                        [nextState.Data.getIndexByID(action.payload.entityID)]: {
                            Custom: {
                                templateConversionStatus: action.payload.status
                            }
                        }
                    }
                });

                if (action.payload.status === RequestStatus.SUCCEEDED) {
                    if (action.payload.createNew) {
                        return deepAssign(nextState, {
                            Data: {
                                [nextState.Data.length]: action.payload.response
                            }
                        });
                    }
                    else {
                        return deepAssign(nextState, {
                            Data: {
                                [nextState.Data.getIndexByID(action.payload.response.ID)]: action.payload.response
                            }
                        });
                    }
                }
            }

            if ((action.type === 'CLONE_WORKSHOP' || action.type === 'PREVIEW_WORKSHOP') && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: [...nextState.Data, ...[action.payload.response].flatten()]
                });
            }

            if (action.type === 'GENERATE_DETAILED_WORKSHOP_WIDGET_CONTENTS') {
                const
                    workshop = nextState.Data.find(({ ContentRows }) => ContentRows.Data.some(({ Widgets }) => Widgets.getByID(action.payload.entityID))),
                    contentRow = workshop?.ContentRows.Data.find(({ Widgets }) => Widgets.getByID(action.payload.entityID)),
                    widget = contentRow?.Widgets.getByID(action.payload.entityID);

                if (workshop && contentRow && widget) {
                    return deepAssign(nextState, {
                        Data: {
                            [nextState.Data.getIndexByID(workshop.ID)]: {
                                ContentRows: {
                                    Data: {
                                        [workshop.ContentRows.Data.getIndexByID(contentRow.ID)]: {
                                            Widgets: entityLoadReducer(wwa, 'GENERATE_DETAILED_WORKSHOP_WIDGET_CONTENTS')(contentRow.Widgets, action)
                                        }
                                    }
                                }
                            }
                        }
                    });
                }
            }

            if (action.type === 'GENERATE_WORKSHOP_WIDGET_IDEAS') {
                const
                    workshop = nextState.Data.find(({ ContentRows }) => ContentRows.Data.some(({ Widgets }) => Widgets.getByID(action.payload.entityID))),
                    contentRow = workshop?.ContentRows.Data.find(({ Widgets }) => Widgets.getByID(action.payload.entityID)),
                    widget = contentRow?.Widgets.getByID(action.payload.entityID);

                if (workshop && contentRow && widget) {
                    return deepAssign(nextState, {
                        Data: {
                            [nextState.Data.getIndexByID(workshop.ID)]: {
                                ContentRows: {
                                    Data: {
                                        [workshop.ContentRows.Data.getIndexByID(contentRow.ID)]: {
                                            Widgets: {
                                                [contentRow.Widgets.getIndexByID(action.payload.entityID)]: {
                                                    Custom: {
                                                        Ideas: {
                                                            Custom: {
                                                                status: action.payload.status
                                                            },
                                                            Data: action.payload.status === RequestStatus.SUCCEEDED ?
                                                                action.payload.response :
                                                                widget.Custom.Ideas?.Data || []
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    });
                }
            }

            if (action.type === 'DROP_WORKSHOP_WIDGET_IDEAS') {
                const
                    workshop = nextState.Data.find(({ ContentRows }) => ContentRows.Data.some(({ Widgets }) => Widgets.getByID(action.payload.entityID))),
                    contentRow = workshop?.ContentRows.Data.find(({ Widgets }) => Widgets.getByID(action.payload.entityID));

                if (workshop && contentRow) {
                    return deepAssign(nextState, {
                        Data: {
                            [nextState.Data.getIndexByID(workshop.ID)]: {
                                ContentRows: {
                                    Data: {
                                        [workshop.ContentRows.Data.getIndexByID(contentRow.ID)]: {
                                            Widgets: {
                                                [contentRow.Widgets.getIndexByID(action.payload.entityID)]: {
                                                    Custom: {
                                                        Ideas: ww.Custom.Ideas
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    });
                }
            }

            if (action.type === 'VOID_ALL_WORKSHOP_WIDGETS' && action.payload.immediately) {
                return deepAssign(nextState, {
                    Data: nextState.Data.map(() => ({
                        ContentRows: {
                            Custom: {
                                status: RequestStatus.UNSYNCED
                            }
                        }
                    }))
                });
            }

            // if ((
            //     action.type === 'EDIT_WORKSHOP' ||
            //     action.type === 'TOGGLE_WORKSHOP_MULTI_USER_STATUS' ||
            //     action.type === 'STORE_WORKSHOP_SCREENSHOT'
            // ) && action.payload.status === RequestStatus.SUCCEEDED) {
            //     return deepAssign(nextState, {
            //         Data: {
            //             [nextState.Data.getIndexByID(action.payload.entityID)]: {
            //                 Updated: new Date()
            //             }
            //         }
            //     });
            // }

            if (action.type === 'DELETE_WORKSHOP' && action.payload.status === RequestStatus.SUCCEEDED) {
                for (let workshopIndex = 0; workshopIndex < nextState.Data.length; workshopIndex++) {
                    const
                        workshop = nextState.Data[workshopIndex],
                        widgets = workshop.ContentRows.Data.map(({ Widgets }) => Widgets).flatten().filter(({ TypeID }) => TypeID === WorkshopWidgetType.RESULTS);

                    for (let widgetIndex = 0; widgetIndex < widgets.length; widgetIndex++) {
                        const widget = widgets[widgetIndex];

                        nextState = deepAssign(nextState, {
                            Data: {
                                [workshopIndex]: {
                                    ContentRows: {
                                        Data: {
                                            [workshop.ContentRows.Data.getIndexByID(widget.ContentRowID)]: {
                                                Widgets: {
                                                    [widgetIndex]: {
                                                        ItemCategoryConnections: widget.ItemCategoryConnections.filter(({ SourceWorkshopID }) =>
                                                            SourceWorkshopID !== action.payload.entityID
                                                        ),
                                                        ResultPhaseSources: widget.ResultPhaseSources.filter(({ SourceWorkshopID }) => SourceWorkshopID !== action.payload.entityID)
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        });
                    }
                }
            }

            if (action.type === 'DELETE_WORKSHOP_WIDGET' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: nextState.Data.map(({ ContentRows }) => ({
                        ContentRows: {
                            Data: ContentRows.Data.map(({ Widgets }) => ({
                                Widgets: Widgets.map(widget => ({
                                    CategoryCreationRuleID: widget.CategoryCreationWidgetID !== action.payload.entityID ? widget.CategoryCreationRuleID : null,
                                    CategoryCreationWidgetID: widget.CategoryCreationWidgetID !== action.payload.entityID ? widget.CategoryCreationWidgetID : null,
                                    ItemCategoryConversionLimit: widget.CategoryCreationWidgetID !== action.payload.entityID ? widget.ItemCategoryConversionLimit : null,
                                    FromCrossPollinationWidgetID: widget.FromCrossPollinationWidgetID !== action.payload.entityID ? widget.FromCrossPollinationWidgetID : null,
                                    ToCrossPollinationWidgetID: widget.ToCrossPollinationWidgetID !== action.payload.entityID ? widget.ToCrossPollinationWidgetID : null,
                                    ItemCategoryConnections: widget.ItemCategoryConnections.filter(({ SourceWidgetID }) => SourceWidgetID !== action.payload.entityID),
                                    RatingPhaseSources: widget.RatingPhaseSources.filter(({ RatedWidgetID }) => RatedWidgetID !== action.payload.entityID),
                                    ResultPhaseSources: widget.ResultPhaseSources.filter(({ SourceWidgetID }) => SourceWidgetID !== action.payload.entityID)
                                }))
                            }))
                        }
                    }))
                });
            }

            if (action.type === 'SEARCH_WORKSHOP_PARTICIPANTS' && action.payload.status === RequestStatus.SUCCEEDED) {
                const groupedParticipants = (action.payload.response as entities.WorkshopParticipant[]).groupBy(({ WorkshopID }) => WorkshopID);

                return deepAssign(nextState, {
                    Data: nextState.Data.leftJoin(groupedParticipants, ({ ID }) => ID, ({ key }) => key, (workshop, updatedParticipants) => {
                        if (!updatedParticipants?.length) { return workshop; }
                        const participants = workshop.Participants.copy();

                        for (let updateIndex = 0; updateIndex < updatedParticipants.length; updateIndex++) {
                            const
                                update = updatedParticipants[updateIndex],
                                participantIndex = participants.getIndexByID(update.ID);

                            if (participantIndex >= 0) {
                                participants.splice(participantIndex, 1, update);
                            }
                            else {
                                participants.push(update);
                            }
                        }

                        return { Participants: participants };
                    })
                });
            }

            if (action.type === 'DELETE_ALL_WORKSHOP_ITEM_CATEGORIES' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: nextState.Data.map(({ ContentRows }) => ({
                        ContentRows: {
                            Data: ContentRows.Data.map(({ Widgets }) => ({
                                Widgets: Widgets.map(widget => ({
                                    ItemCategoryConnections: widget.ItemCategoryConnections.map(source => ({
                                        SourceCategoryID: !action.payload.entityIDs.includes(source.SourceCategoryID) ? source.SourceCategoryID : null
                                    })),
                                    ResultPhaseSources: widget.ResultPhaseSources.map(source => ({
                                        SourceCategoryID: !action.payload.entityIDs.includes(source.SourceCategoryID) ? source.SourceCategoryID : null
                                    }))
                                }))
                            }))
                        }
                    }))
                });
            }

            if (action.type === 'DELETE_STRATEGIC_GOAL' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, { Data: nextState.Data.filter(({ StrategicGoalID }) => StrategicGoalID !== action.payload.entityID) });
            }

            if (action.type === 'DELETE_STRATEGIC_GOAL_MILESTONE' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, { Data: nextState.Data.filter(({ MilestoneID }) => MilestoneID !== action.payload.entityID) });
            }

            if (action.type.includes('WORKSHOP') && action.payload?.status === RequestStatus.FAILED) {
                return deepAssign(nextState, { Custom: { expires: new Date() } });
            }

            if (action.type === 'DELETE_STRATEGY_DESCRIPTION' && action.payload.status === RequestStatus.SUCCEEDED ||
                action.type === 'VOID_ALL_WORKSHOPS') {
                return entityVoidAllReducer(wn, action.type)(state, action);
            }

            if (action.type === 'DOWNLOAD_WORKSHOP_DATA_ZIP') {
                return deepAssign(nextState, {
                    Data: {
                        [nextState.Data.getIndexByID(action.payload.entityID)]: {
                            Custom: {
                                exportStatus: action.payload.status
                            }
                        }
                    }
                });
            }

            return nextState;
        },
        entityDeleteReducer(wn, 'DELETE_WORKSHOP'),
        (state, _action) => {
            const nextState = state || wn;
            return deepAssign(nextState, {
                Data: nextState.Data.map(({ Phases }) => ({
                    IsCompleted: Phases.some(({ IsPublished, IsClosed }) => IsPublished || IsClosed) && Phases.every(({ IsPublished, IsClosed }) => !IsPublished || IsClosed)
                }))
            });
        }
    )),
    workshopBackgroundImages: switchReducers(wbia,
        synchronizableEnumReducer(wbia, 'LOAD_ALL_WORKSHOP_BACKGROUND_IMAGES', WorkshopBackgroundImage as any, 'WorkshopBackgroundImage'),
        (state, action) => {
            const nextState = state || wbia;

            if (action.type === 'LOAD_WORKSHOP_BACKGROUND_IMAGE_AS_BASE64_STRING' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: {
                        [nextState.Data.getIndexByID(action.payload.entityID)]: {
                            Custom: {
                                base64Image: action.payload.response
                            }
                        }
                    }
                });
            }

            return nextState;
        }
    ),
    workshopVersionHistoryChangeTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopVersionHistoryChangeType),
        'LOAD_ALL_WORKSHOP_VERSION_HISTORY_CHANGE_TYPES',
        WorkshopVersionHistoryChangeType as any,
        'WorkshopVersionHistoryChangeType'
    ),
    workshopPhaseContentRowLayouts: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopPhaseContentRowLayout),
        'LOAD_ALL_WORKSHOP_PHASE_CONTENT_ROW_LAYOUTS',
        WorkshopPhaseContentRowLayout as any,
        'WorkshopPhaseContentRowLayout'
    ),
    workshopCanvasItemTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopCanvasItemType),
        'LOAD_ALL_WORKSHOP_CANVAS_ITEM_TYPES',
        WorkshopCanvasItemType as any,
        'WorkshopCanvasItemType'
    ),
    workshopWidgetTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopWidgetType),
        'LOAD_ALL_WORKSHOP_WIDGET_TYPES',
        WorkshopWidgetType as any,
        'WorkshopWidgetType'
    ),
    workshopWidgetStatuses: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopWidgetStatus),
        'LOAD_ALL_WORKSHOP_WIDGET_STATUSES',
        WorkshopWidgetStatus as any,
        'WorkshopWidgetStatus'
    ),
    workshopExternalContentTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopExternalContentType),
        'LOAD_ALL_WORKSHOP_EXTERNAL_CONTENT_TYPES',
        WorkshopExternalContentType as any,
        'WorkshopExternalContentType'
    ),
    workshopPhaseContentCreationRules: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopPhaseContentCreationRule),
        'LOAD_ALL_WORKSHOP_PHASE_CONTENT_CREATION_RULES',
        WorkshopPhaseContentCreationRule as any,
        'WorkshopPhaseContentCreationRule'
    ),
    workshopPrioritizationVotingTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopPrioritizationVotingType),
        'LOAD_ALL_WORKSHOP_PRIORITIZATION_VOTING_TYPES',
        WorkshopPrioritizationVotingType as any,
        'WorkshopPrioritizationVotingType'
    ),
    workshopRatingPhaseWeightedLevels: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopRatingPhaseWeightedLevel),
        'LOAD_ALL_WORKSHOP_RATING_PHASE_WEIGHTED_LEVELS',
        WorkshopRatingPhaseWeightedLevel as any,
        'WorkshopRatingPhaseWeightedLevel'
    ),
    workshopQuestionnaireQuestionTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopQuestionnaireQuestionType),
        'LOAD_ALL_WORKSHOP_QUESTIONNAIRE_QUESTION_TYPES',
        WorkshopQuestionnaireQuestionType as any,
        'WorkshopQuestionnaireQuestionType'
    ),
    workshopResultPresentationTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.WorkshopResultPresentationType),
        'LOAD_ALL_WORKSHOP_RESULT_PRESENTATION_TYPES',
        WorkshopResultPresentationType as any,
        'WorkshopResultPresentationType'
    ),
    workshopExternalUrlTest: switchReducers(wpect,
        networkActionReducer(wpect, 'TEST_WORKSHOP_WIDGET_EXTERNAL_CONTENT_URL'),
        (state, action) => {
            const defaultState = state || wpect;

            if (action.type === 'TEST_WORKSHOP_WIDGET_EXTERNAL_CONTENT_URL' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(defaultState, { Data: action.payload.response });
            }

            if (action.type === 'DROP_WORKSHOP_WIDGET_EXTERNAL_CONTENT_TEST_RESULTS') {
                return wpect;
            }

            return defaultState;
        }
    ),
    workshopCustomBackgroundImage: (state, action) => {
        let nextState = state || wcbi;

        if (action.type === 'GENERATE_CUSTOM_WORKSHOP_BACKGROUND_IMAGE' ||
            action.type === 'LOAD_WORKSHOP_BACKGROUND_IMAGE_AS_BASE64_STRING') {
            nextState = deepAssign(nextState, { Custom: { status: action.payload.status } });

            if (action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, { Data: action.payload.response });
            }
        }

        if (action.type === 'SET_CUSTOM_WORKSHOP_BACKGROUND_IMAGE') {
            return deepAssign(nextState, {
                Custom: {
                    status: RequestStatus.SUCCEEDED
                },
                Data: action.payload.base64Image
            });
        }

        if (action.type === 'CLEAR_CUSTOM_WORKSHOP_BACKGROUND_IMAGE' ||
            action.type === 'STORE_CUSTOM_WORKSHOP_BACKGROUND_IMAGE' && action.payload.status === RequestStatus.SUCCEEDED ||
            action.type === 'STORE_CUSTOM_WORKSHOP_PHASE_BACKGROUND_IMAGE' && action.payload.status === RequestStatus.SUCCEEDED) {
            return wcbi;
        }

        return nextState;
    },
    workspaceMindmaps: switchReducers(wms,
        entityLoadAllReducer(wms, 'LOAD_ALL_WORKSPACE_MINDMAPS'),
        entityLoadReducer(wms, 'LOAD_WORKSPACE_MINDMAP'),
        entityAddReducer(wms, 'ADD_WORKSPACE_MINDMAP'),
        entityEditReducer(wms, 'EDIT_WORKSPACE_MINDMAP'),
        childrenReducer(wms,
            switchReducers(wm,
                networkActionReducer(wm, 'CLONE_WORKSPACE_MINDMAP'),
                combineReducers(wm, {
                    NodeTypes: switchReducers(wmnts,
                        entityLoadAllReducer(wmnts, 'LOAD_ALL_WORKSPACE_MINDMAP_NODE_TYPES'),
                        entityAddAllReducer(wmnts, 'ADD_ALL_WORKSPACE_MINDMAP_NODE_TYPES'),
                        entityEditAllReducer(wmnts, 'EDIT_ALL_WORKSPACE_MINDMAP_NODE_TYPES'),
                        entityDeleteAllReducer(wmnts, 'DELETE_ALL_WORKSPACE_MINDMAP_NODE_TYPES')
                    ),
                    Nodes: sortedEntitiesReducer(wmns, 'Name', switchReducers(wmns,
                        entityLoadAllReducer(wmns, 'LOAD_WORKSPACE_MINDMAP_NODE_SLICE'),
                        entityLoadReducer(wmns, 'LOAD_WORKSPACE_MINDMAP_NODE'),
                        entityAddReducer(wmns, 'ADD_WORKSPACE_MINDMAP_NODE'),
                        entityEditReducer(wmns, 'EDIT_WORKSPACE_MINDMAP_NODE'),
                        entityDeleteAllReducer(wmns, 'DELETE_ALL_WORKSPACE_MINDMAP_NODES'),
                        entityDeleteReducer(wmns, 'DELETE_WORKSPACE_MINDMAP_NODE'),
                        (state, action) => {
                            const nextState = state || wmns;

                            if (action.type === 'DELETE_WORKSPACE_MINDMAP_NODE' && action.payload.status === RequestStatus.SUCCEEDED) {
                                const nodes = nextState.Data.map(node => deepAssign(node, { FatherID: node.FatherID !== action.payload.entityID ? node.FatherID : null }));
                                return deepAssign(nextState, { Data: nodes });
                            }

                            if (action.type === 'ADIT_ALL_WORKSPACE_MINDMAP_NODE_CUSTOMS') {
                                const entities = (action.payload.entities || []) as any as entities.WorkspaceMindmapNode[];

                                return deepAssign(nextState, {
                                    Data: nextState.Data.map(node => deepAssign(node, {
                                        Custom: entities.find(entity => entity.ID === node.ID)?.Custom || node.Custom
                                    }))
                                });
                            }

                            return nextState;
                        },
                        entityVoidAllReducer(wmns, 'VOID_ALL_WORKSPACE_MINDMAP_NODES')
                    ))
                })
            ),
            (state, action) => {
                if (action.type === 'CLONE_WORKSPACE_MINDMAP') {
                    return state.Data.getIndexByID(action.payload?.entityID);
                }

                return state.Data.getIndexByID(action.payload?.parentID);
            }
        ),
        entityDeleteReducer(wms, 'DELETE_WORKSPACE_MINDMAP'),
        (state, action) => {
            const nextState = state || wms;

            if (action.type === 'CLONE_WORKSPACE_MINDMAP' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: {
                        [nextState.Data.length]: action.payload.response
                    }
                });
            }

            if (action.type === 'STORE_WORKSPACE_MINDMAP_SCREENSHOT') {
                let entity = nextState.Data.getByID(action.payload.entityID);

                if (entity) {
                    entity = deepAssign(entity, { Custom: { status: action.payload.status } });

                    if (action.payload.status === RequestStatus.PENDING) { entity = deepAssign(entity, { PreviewImageTakenAt: new Date() }); }
                    if (action.payload.status === RequestStatus.SUCCEEDED) { entity = deepAssign(entity, { PreviewBlobStorageUrl: action.payload.response }); }

                    return deepAssign(nextState, { Data: { [nextState.Data.getIndexByID(entity.ID)]: entity } });
                }
            }

            if (action.type === 'ADD_WORKSHOP_WIDGET' && action.payload.entity.TypeID === WorkshopWidgetType.MINDMAP && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, { Custom: { status: RequestStatus.UNSYNCED } });
            }

            if (action.type === 'CLEAR_ALL_WORKSHOP_DATA' && action.payload.status === RequestStatus.SUCCEEDED) {
                return deepAssign(nextState, {
                    Data: nextState.Data.map(() => ({
                        Nodes: {
                            Custom: {
                                status: RequestStatus.UNSYNCED
                            }
                        }
                    }))
                });
            }

            return nextState;
        },
        entityVoidAllReducer(wms, 'VOID_ALL_WORKSPACE_MINDMAPS')
    ),
    groupCollaborationRestorePoint: (state, action) => {
        const nextState = state || gcrp;

        if (action.type === 'SET_GROUP_COLLABORATION_RESTORE_POINT') {
            return { ...nextState, [action.payload.path]: action.payload.options };
        }

        return nextState;
    }
});
